export type TypeObserveOptionSelectedConfig = {
    fnSelected?: (selected: boolean) => void;
};

// ----------------------------------------------------------------------------

const modifiedElements = new WeakMap<HTMLOptionElement, PropertyDescriptor>();

// ----------------------------------------------------------------------------

function observeOptionSelected(option: HTMLOptionElement, config: TypeObserveOptionSelectedConfig = {}): () => void {
    if (modifiedElements.has(option)) {
        return (): any => {
            // 'The "selected" property has already been observed for this option element'
        };
    }

    const proto = Object.getPrototypeOf(option);
    const original = Object.getOwnPropertyDescriptor(proto, 'selected');

    if (!original || typeof original.get !== 'function' || typeof original.set !== 'function') {
        throw new Error('The "selected" property does not have valid getter/setter methods');
    }

    const originalGet = original.get.bind(option);
    const originalSet = original.set.bind(option);

    modifiedElements.set(option, original);

    Object.defineProperty(option, 'selected', {
        get: function (this: HTMLOptionElement): any {
            return originalGet();
        },
        set: function (this: HTMLOptionElement, newSelected: boolean): any {
            const oldSelected = originalGet();

            if (oldSelected === newSelected) {
                return;
            }

            originalSet(newSelected);

            if (typeof config.fnSelected === 'function') {
                config.fnSelected.call(this, newSelected);
            }
        },
        configurable: true,
        enumerable: true,
    });

    return (): any => {
        const originalDescriptor = modifiedElements.get(option);

        if (originalDescriptor) {
            Object.defineProperty(option, 'selected', originalDescriptor);

            modifiedElements.delete(option);
        }
    };
}

// ----------------------------------------------------------------------------

export default observeOptionSelected;
